import {
	AgencyId,
	type BondRequestBondType,
	bondRequestBondTypeDef,
	type CompanyContactId,
	type ContractBondSubtype,
	ContractSuretyType,
	type Currency,
	commercialSuretyBondTypeDef,
	contractBondSubtypeDef,
	contractCategoryBondTypeDef,
	currencySchema,
	type FinalContractSuretyBondFormType,
	finalContractSuretyBondFormTypeSchema,
	type ISODate,
	isCommercialSuretyBondType,
	type NaicsCodeId,
	naicsCodeId,
	positiveCurrencySchema,
	SuretyAccountId,
	type SuretyBondId,
	suretyBondId,
	type UserId,
} from "@inrev/common";
import { getAllowedBondFormUploadTypes } from "@portal/views/shared/bond/bondUtils";
import { z } from "zod";
import type { BondFormData } from "../../../types";
import { resolveContractSuretyType } from "./bondRequestSubmissionBondTypeUtils";

export type SubmissionFormData = {
	accountId?: SuretyAccountId;
	agencyId?: AgencyId;
	principal?: CompanyContactId;
	bondType?: BondRequestBondType;
	contractBondType?: ContractBondSubtype;
	agentId?: UserId;
	projectName?: string;
	bidAmount?: Currency;
	submissionNarrative?: string;
	scopeOfWork?: NaicsCodeId[];
	completionBond?: boolean;
	tListingRequired?: boolean;
	bidDate?: ISODate;
	finalBondFormType?: FinalContractSuretyBondFormType;
	bidBondId?: SuretyBondId;
	actualBidAmount?: Currency;
	actualBidDate?: ISODate;
	secondBid?: Currency;
	bondAmount?: Currency;
	contractAmount?: Currency;
	contractAmountEdited?: boolean;
	effectiveDate?: ISODate;
	bondForm?: BondFormData;
};

export const ACCOUNT_FIELDS = ["accountId", "agencyId", "agentId"] as const;
export const PRINCIPAL_FIELDS = [
	"principal",
	"bondType",
	"contractBondType",
	"bidBondId",
	"actualBidAmount",
] as const;
export const FILES_STEP_FIELDS = ["projectName", "bidAmount"] as const;
export const B2F_FILES_STEP_FIELDS = [
	"projectName",
	"actualBidAmount",
	"secondBid",
	"bondAmount",
	"contractAmount",
] as const;
export const COMMERCIAL_INFO_FIELDS = ["bondAmount", "effectiveDate"] as const;

const REQUIRED_PRESENCE_FIELDS = [
	"accountId",
	"agencyId",
	"principal",
	"bondType",
	"agentId",
] as const;

const optionalContractBondFields = {
	contractBondType: z.enum(contractBondSubtypeDef).nullish(),
	bidBondId: suretyBondId.nullish(),
	actualBidAmount: positiveCurrencySchema.nullish(),
};

const bondTypeSubmissionSchema = z.discriminatedUnion("bondType", [
	z.object({
		bondType: z.enum(contractCategoryBondTypeDef),
		...optionalContractBondFields,
		contractBondType: z.enum(contractBondSubtypeDef, {
			error: "Required",
		}),
	}),
	z.object({
		bondType: z.enum(commercialSuretyBondTypeDef),
		...optionalContractBondFields,
		bondAmount: z.number({ error: "Required" }).pipe(positiveCurrencySchema),
		effectiveDate: z.string({ error: "Required" }).min(1, {
			error: "Required",
		}),
	}),
	z.object({
		bondType: z.literal(ContractSuretyType.bid_to_final),
		contractBondType: z.enum(contractBondSubtypeDef).nullish(),
		bidBondId: z.string({ error: "Required" }).pipe(suretyBondId),
		actualBidAmount: z.number({ error: "Required" }).pipe(positiveCurrencySchema),
		bondAmount: z.number({ error: "Required" }).pipe(positiveCurrencySchema),
		contractAmount: z.number({ error: "Required" }).pipe(positiveCurrencySchema),
		secondBid: positiveCurrencySchema.nullish(),
	}),
	z.object({
		bondType: z.literal(ContractSuretyType.bid),
		...optionalContractBondFields,
		bidAmount: z.number({ error: "Required" }).pipe(positiveCurrencySchema),
	}),
	z.object({
		bondType: z.literal(ContractSuretyType.final),
		...optionalContractBondFields,
	}),
]);

export const buildMinimalSubmissionFormSchema = () =>
	z
		.object({
			accountId: z.string().nullish(),
			agencyId: z.string().nullish(),
			principal: z.string().nullish(),
			bondType: z.enum(bondRequestBondTypeDef).nullish(),
			contractBondType: z.enum(contractBondSubtypeDef).nullish(),
			agentId: z.string().nullish(),
			projectName: z.string().nullish(),
			bidAmount: currencySchema.nullish(),
			submissionNarrative: z.string().nullish(),
			scopeOfWork: z.array(naicsCodeId).nullish(),
			completionBond: z.boolean().nullish(),
			tListingRequired: z.boolean().nullish(),
			bidDate: z.string().nullish(),
			finalBondFormType: finalContractSuretyBondFormTypeSchema.nullish(),
			bidBondId: suretyBondId.nullish(),
			actualBidAmount: positiveCurrencySchema.nullish(),
			actualBidDate: z.string().nullish(),
			secondBid: positiveCurrencySchema.nullish(),
			bondAmount: positiveCurrencySchema.nullish(),
			contractAmount: positiveCurrencySchema.nullish(),
			contractAmountEdited: z.boolean().nullish(),
			effectiveDate: z.string().nullish(),
			// Shape is owned by `BondFormSelectInput`; only its consistency with `bondType` is
			// checked in superRefine. The bond form itself is optional in this flow.
			bondForm: z.custom<BondFormData>().nullish(),
		})
		.superRefine((val, ctx) => {
			// accountId/agencyId/agentId are only collected in the admin flow. The agent flow never
			// triggers these fields, so the resolver's scopeToNames filtering keeps these issues from
			// surfacing there — do not rely on a full safeParse of this schema for the agent flow.
			for (const field of REQUIRED_PRESENCE_FIELDS) {
				if (!val[field]) {
					ctx.addIssue({ code: "custom", path: [field], message: "Required" });
				}
			}
			const isCommercial = isCommercialSuretyBondType(val.bondType);
			if (!isCommercial && !val.projectName?.trim()) {
				ctx.addIssue({
					code: "custom",
					path: ["projectName"],
					message: "Required",
				});
			}
			const contractSuretyType = resolveContractSuretyType(val.bondType, val.contractBondType);
			if (
				contractSuretyType === ContractSuretyType.bid &&
				val.bondType !== ContractSuretyType.bid &&
				(val.bidAmount == null || val.bidAmount <= 0)
			) {
				ctx.addIssue({
					code: "custom",
					path: ["bidAmount"],
					message: "Required",
				});
			}
			const uploadType = val.bondForm?.upload.type;
			if (contractSuretyType && uploadType) {
				const allowedTypes = getAllowedBondFormUploadTypes(contractSuretyType);
				if (!(uploadType in allowedTypes)) {
					ctx.addIssue({
						code: "custom",
						path: ["bondForm"],
						message: "Bond form does not match the selected bond type",
					});
				}
			}

			if (!val.bondType) return;
			const bondTypeResult = bondTypeSubmissionSchema.safeParse(val);
			if (bondTypeResult.success) return;
			for (const issue of bondTypeResult.error.issues) {
				ctx.addIssue({
					code: "custom",
					path: issue.path,
					message: issue.message,
				});
			}
		});
